"use client";

import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCopy } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import {
  CHAT_ASSISTANT_COPY_BUTTON_CLASS,
  CHAT_ASSISTANT_COPY_FADE_SURFACE_CLASS,
} from "./chatMessageChrome";

type ChatAssistantCopyButtonProps = {
  text: string;
  className?: string;
};

export function ChatAssistantCopyButton({ text, className }: ChatAssistantCopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current !== null) {
        window.clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const onCopy = useCallback(async () => {
    if (!text.trim()) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      if (copiedTimerRef.current !== null) {
        window.clearTimeout(copiedTimerRef.current);
      }
      setCopied(true);
      copiedTimerRef.current = window.setTimeout(() => {
        copiedTimerRef.current = null;
        setCopied(false);
      }, 2000);
    } catch {
      setCopied(false);
    }
  }, [text]);

  return (
    <div className={clsx(CHAT_ASSISTANT_COPY_FADE_SURFACE_CLASS, className)}>
      <Button
        type="button"
        plain
        data-testid="chat-assistant-copy"
        title={copied ? "Copied" : "Copy message"}
        aria-label={copied ? "Copied" : "Copy message"}
        className={CHAT_ASSISTANT_COPY_BUTTON_CLASS}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          void onCopy();
        }}
        icon={
          <FontAwesomeIcon
            icon={copied ? faCheck : faCopy}
            className="h-3 w-3 text-slate-400 dark:text-slate-500"
          />
        }
      >
        {""}
      </Button>
    </div>
  );
}
